import Switch from '@/components/Switch/Switch';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { useAdvancedEditorContext } from '@/pages/AdvancedEditor/context/useAdvancedEditorContext';
import { getPageProperty, PolotnoStoreActivePageKeys, PolotnoStoreKeys, usePolotnoState } from '@/pages/AdvancedEditor/hooks';
import { AudioData } from '@/pages/AdvancedEditor/hooks/useUploadAudio';
import { ScriptType } from '@d-id/types';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

const chineseRegExp = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/g;

const isShortText = (str: string) => {
    const text = str.replaceAll('⏱', '');
    if (text.split(' ').filter(Boolean).length >= 6) {
        return false;
    }
    const isChiness = chineseRegExp.test(text);
    if (isChiness && text.length >= 6) {
        return false;
    }
    return true;
};

const validateIfSubtitilesAvailable = (scriptType: ScriptType, text: string, audioData: AudioData | null, numPages: number) => {
    if (scriptType === ScriptType.text) {
        if (isShortText(text))
            return numPages > 1
                ? 'Subtitles require at least 6 words per scene. Add more words to enable subtitles.'
                : 'Minimum 6 words required to add subtitles to the video';
    } else if (audioData && audioData.duration < 3)
        return numPages > 1
            ? 'Subtitles require at least 3 seconds of audio per scene. Add longer audio to enable subtitles.'
            : 'Minimum 3 seconds required to add subtitles to the video';
};

export const SubtitleToggle = observer(() => {
    const { store } = useAdvancedEditorContext();
    const [subtitlesActive, setSubtitlesActive] = usePolotnoState(PolotnoStoreKeys.subtitlesActive);

    const handleSwitchChange = (_: unknown, checked: boolean) => {
        setSubtitlesActive(checked);
    };

    const pagesValidation = store.pages
        .map(page =>
            validateIfSubtitilesAvailable(
                getPageProperty(page, PolotnoStoreActivePageKeys.selectedScript),
                getPageProperty(page, PolotnoStoreActivePageKeys.textInput),
                getPageProperty(page, PolotnoStoreActivePageKeys.uploadedAudio),
                store.pages.length
            )
        )
        .find(validation => validation !== undefined);

    useEffect(() => {
        if (pagesValidation) {
            setSubtitlesActive(false);
        }
    }, [pagesValidation]);

    if (pagesValidation) {
        return (
            <CustomTooltip title={pagesValidation} disableHoverListener={!pagesValidation}>
                <Switch checked={false} onChange={handleSwitchChange} disabled />
            </CustomTooltip>
        );
    }

    return <Switch checked={subtitlesActive} onChange={handleSwitchChange} />;
});
